import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import { comics, getCategoryName, getComicById } from "@/mocks/comics";

export default function Article() {
  const { id } = useParams();
  const comic = getComicById(Number(id));
  const [liked, setLiked] = useState(false);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="container-site flex min-h-[70vh] flex-col items-center justify-center pt-16 text-center">
          <i className="ri-search-line text-5xl text-foreground-300"></i>
          <h1 className="mt-6 text-xl font-semibold text-foreground-950">没有找到这部漫画</h1>
          <p className="mt-2 text-sm text-foreground-600">该漫画可能已下架或链接有误。</p>
          <Link
            to="/category/all"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回漫画分类
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5);
  const categoryName = getCategoryName(comic.category);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 漫画岛高清漫画`,
    description: comic.description,
    image: comic.cover,
    author: { "@type": "Person", name: comic.author },
    publisher: { "@type": "Organization", name: "漫画岛" },
    dateModified: "2026-08-16",
    keywords: `${comic.title},漫画岛,${categoryName}漫画,高清漫画`,
    mainEntityOfPage: "https://rejqj97.cn/",
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />

      <main className="bg-background-100 pt-16">
        <nav aria-label="面包屑" className="container-site py-4">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-700">
            <li><Link to="/" className="whitespace-nowrap hover:text-primary-500">首页</Link></li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li>
              <Link to="/category/all" className="whitespace-nowrap hover:text-primary-500">漫画分类</Link>
            </li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li>
              <Link to={`/category/${comic.category}`} className="whitespace-nowrap hover:text-primary-500">
                {categoryName}
              </Link>
            </li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li className="whitespace-nowrap font-semibold text-primary-600" aria-current="page">
              {comic.title}
            </li>
          </ol>
        </nav>

        <section className="container-site">
          <div className="grid gap-8 rounded-lg bg-background-50 p-6 md:grid-cols-[280px_1fr] md:p-8">
            <div className="mx-auto w-48 md:w-full">
              <img
                src={comic.cover}
                alt={`${comic.title} 高清漫画封面`}
                className="aspect-[3/4] w-full rounded-lg object-cover object-top"
              />
            </div>

            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                  <i className="ri-fire-fill"></i>
                  {comic.hot ? "热门" : "精选"}
                </span>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                    comic.status === "连载中"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-secondary-100 text-secondary-800"
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <h1 className="mt-3 text-2xl font-semibold text-foreground-950 md:text-3xl">{comic.title}</h1>
              <p className="mt-1 text-sm text-foreground-600">
                {comic.author} · {categoryName}漫画
              </p>

              <div className="mt-5 flex flex-wrap gap-3 text-sm text-foreground-700">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-950">{comic.rating.toFixed(1)}</strong> 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {comic.views} 人气
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-book-2-line"></i>
                  {comic.chapters.length} 话
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  更新于 {comic.lastUpdate}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span key={tag} className="whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                {comic.description}
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="#chapter"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-book-open-fill"></i>
                  开始阅读
                </Link>
                <button
                  type="button"
                  onClick={() => setLiked((v) => !v)}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-7 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                    liked
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 text-foreground-800 hover:bg-background-100"
                  }`}
                >
                  <i className={liked ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {liked ? "已收藏" : "收藏"}
                  <span className="text-xs opacity-80">{comic.likes.toLocaleString()}</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="chapter" className="container-site py-10 md:py-12">
          <h2 className="mb-5 text-lg font-semibold text-foreground-950 md:text-xl">章节列表</h2>
          <div className="grid gap-2 rounded-lg bg-background-50 p-4 sm:grid-cols-2 lg:grid-cols-3 md:p-5">
            {comic.chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to="/category/all"
                className="flex items-center justify-between gap-3 rounded-md px-4 py-3 text-sm transition-colors hover:bg-primary-50"
                title={`阅读 ${comic.title} ${chapter.title}`}
              >
                <span className="flex items-center gap-2 truncate text-foreground-800">
                  <i className="ri-file-text-line text-primary-500"></i>
                  <span className="truncate">{chapter.title}</span>
                </span>
                <span className="shrink-0 text-xs text-foreground-500">{chapter.date}</span>
              </Link>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="container-site pb-16 md:pb-20">
            <div className="mb-5 flex items-end justify-between">
              <h2 className="text-lg font-semibold text-foreground-950 md:text-xl">同类佳作推荐</h2>
              <Link
                to={`/category/${comic.category}`}
                className="hidden items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-500 sm:inline-flex cursor-pointer"
              >
                更多{categoryName}漫画
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}